// FILE: quotedContext.ts
// Purpose: One model for every kind of quoted context the composer can carry -- a terminal
//   selection or a slice of a transcript message -- with the inline placeholder arithmetic and
//   the prompt block each kind materializes into.
// Layer: Chat composer helpers
// Exports: quoted-context types, placeholder helpers, block builders, and the trailing parser.

import { type MessageId, type ThreadId } from "@taskin-code/contracts";

// U+FFFC OBJECT REPLACEMENT CHARACTER. One per quoted context, positioned in the prompt where
// the chip sits. The Nth placeholder maps to the Nth entry of the context array, so array order
// is load-bearing.
export const INLINE_QUOTED_CONTEXT_PLACEHOLDER = "\uFFFC";

// ── Model ───────────────────────────────────────────────────────────────────────

export interface TerminalQuotedContextSelection {
  kind: "terminal";
  terminalId: string;
  terminalLabel: string;
  lineStart: number;
  lineEnd: number;
  text: string;
}

export interface MessageQuotedContextSelection {
  kind: "message";
  messageId: MessageId;
  messageRole: "assistant" | "user";
  text: string;
}

export type QuotedContextKind = "terminal" | "message";

export type QuotedContextSelection = TerminalQuotedContextSelection | MessageQuotedContextSelection;

export type QuotedContextDraft = QuotedContextSelection & {
  id: string;
  threadId: ThreadId;
  createdAt: string;
};

export type TerminalQuotedContextDraft = TerminalQuotedContextSelection & {
  id: string;
  threadId: ThreadId;
  createdAt: string;
};

export interface ParsedQuotedContextEntry {
  header: string;
  body: string;
}

// ── Text normalization ──────────────────────────────────────────────────────────

export function normalizeQuotedContextText(text: string): string {
  return text.replace(/\r\n/g, "\n").replace(/^\n+|\n+$/g, "");
}

export function hasQuotedContextText(context: { text: string }): boolean {
  return normalizeQuotedContextText(context.text).length > 0;
}

// A context without snapshot text is expired: terminal selections deliberately do not persist
// their text (the buffer is volatile), so a rehydrated one has nothing left to quote.
export function isQuotedContextExpired(context: { text: string }): boolean {
  return !hasQuotedContextText(context);
}

export function filterQuotedContextsWithText<T extends { text: string }>(
  contexts: ReadonlyArray<T>,
): T[] {
  return contexts.filter((context) => hasQuotedContextText(context));
}

export function normalizeQuotedContextSelection(
  selection: QuotedContextSelection,
): QuotedContextSelection | null {
  const text = normalizeQuotedContextText(selection.text);
  if (text.length === 0) {
    return null;
  }
  if (selection.kind === "message") {
    return { ...selection, text };
  }
  const terminalId = selection.terminalId.trim();
  const terminalLabel = selection.terminalLabel.trim();
  if (terminalId.length === 0 || terminalLabel.length === 0) {
    return null;
  }
  const lineStart = Math.max(1, Math.floor(selection.lineStart));
  const lineEnd = Math.max(lineStart, Math.floor(selection.lineEnd));
  return { kind: "terminal", terminalId, terminalLabel, lineStart, lineEnd, text };
}

// ── Labels ──────────────────────────────────────────────────────────────────────

export function formatTerminalQuotedContextRange(selection: {
  lineStart: number;
  lineEnd: number;
}): string {
  return selection.lineStart === selection.lineEnd
    ? `line ${selection.lineStart}`
    : `lines ${selection.lineStart}-${selection.lineEnd}`;
}

// Labels are derived from identity alone, never from the quoted text, so callers that only hold
// a parsed header (the transcript re-rendering a sent message) can format one too.
export type QuotedContextLabelInput =
  | { kind: "terminal"; terminalLabel: string; lineStart: number; lineEnd: number }
  | { kind: "message"; messageRole: "assistant" | "user" };

const MESSAGE_QUOTE_HEADERS = {
  assistant: "Assistant message",
  user: "Your message",
} as const;

export function formatQuotedContextLabel(selection: QuotedContextLabelInput): string {
  if (selection.kind === "message") {
    return MESSAGE_QUOTE_HEADERS[selection.messageRole];
  }
  return `${selection.terminalLabel} ${formatTerminalQuotedContextRange(selection)}`;
}

// A sent message keeps only the parsed header, so the transcript has to recover the kind from the
// label. Both sides go through MESSAGE_QUOTE_HEADERS, so the two cannot drift apart.
export function inferQuotedContextKindFromHeader(header: string): QuotedContextKind {
  const trimmed = header.trim();
  return Object.values(MESSAGE_QUOTE_HEADERS).some((candidate) => candidate === trimmed)
    ? "message"
    : "terminal";
}

// The inline form replaces the placeholder in the text actually sent to the model, so it has to
// read as a compact reference rather than a sentence.
export function formatInlineQuotedContextLabel(selection: QuotedContextLabelInput): string {
  if (selection.kind === "message") {
    return selection.messageRole === "assistant" ? "@assistant" : "@you";
  }
  const terminalLabel = selection.terminalLabel.trim().toLowerCase().replace(/\s+/g, "-");
  const range =
    selection.lineStart === selection.lineEnd
      ? `${selection.lineStart}`
      : `${selection.lineStart}-${selection.lineEnd}`;
  return `@${terminalLabel}:${range}`;
}

function previewQuotedContextText(text: string): string {
  const normalized = normalizeQuotedContextText(text);
  if (normalized.length === 0) {
    return "";
  }
  const lines = normalized.split("\n");
  const visibleLines = lines.slice(0, 3);
  if (lines.length > 3) {
    visibleLines.push("...");
  }
  const preview = visibleLines.join("\n");
  return preview.length > 180 ? `${preview.slice(0, 177)}...` : preview;
}

export function buildQuotedContextPreviewTitle(
  contexts: ReadonlyArray<QuotedContextSelection>,
): string | null {
  if (contexts.length === 0) {
    return null;
  }
  const previews = contexts
    .map((context) => {
      const normalized = normalizeQuotedContextSelection(context);
      if (!normalized) {
        return null;
      }
      const preview = previewQuotedContextText(normalized.text);
      return preview.length > 0
        ? `${formatQuotedContextLabel(normalized)}\n${preview}`
        : formatQuotedContextLabel(normalized);
    })
    .filter((value): value is string => value !== null)
    .join("\n\n");
  return previews.length > 0 ? previews : null;
}

// ── Materialized blocks ─────────────────────────────────────────────────────────

// Terminal blocks keep the historical tag. Renaming it would break the transcript rendering of
// every user message already persisted in Postgres, which parses this exact tag back out.
const TERMINAL_QUOTED_CONTEXT_TAG = "terminal_context";
const MESSAGE_QUOTED_CONTEXT_TAG = "quoted_message";

const QUOTED_CONTEXT_TAGS = [TERMINAL_QUOTED_CONTEXT_TAG, MESSAGE_QUOTED_CONTEXT_TAG] as const;

function quotedContextTagFor(selection: QuotedContextSelection): string {
  return selection.kind === "message" ? MESSAGE_QUOTED_CONTEXT_TAG : TERMINAL_QUOTED_CONTEXT_TAG;
}

// Both kinds share the "- header:" + two-space-indented body layout so that one parser can read
// either block back out of a persisted message.
function buildQuotedContextBodyLines(selection: QuotedContextSelection): string[] {
  const normalizedText = normalizeQuotedContextText(selection.text);
  if (selection.kind === "message") {
    return normalizedText.split("\n").map((line) => `  ${line}`);
  }
  return normalizedText
    .split("\n")
    .map((line, index) => `  ${selection.lineStart + index} | ${line}`);
}

function buildSingleTagQuotedContextBlock(
  tag: string,
  contexts: ReadonlyArray<QuotedContextSelection>,
): string {
  if (contexts.length === 0) {
    return "";
  }
  const lines: string[] = [];
  for (let index = 0; index < contexts.length; index += 1) {
    const context = contexts[index]!;
    lines.push(`- ${formatQuotedContextLabel(context)}:`);
    lines.push(...buildQuotedContextBodyLines(context));
    if (index < contexts.length - 1) {
      lines.push("");
    }
  }
  return [`<${tag}>`, ...lines, `</${tag}>`].join("\n");
}

// Groups by tag so a prompt carrying both a terminal selection and a message quote emits one
// block per kind, in a stable order, instead of interleaving tags.
export function buildQuotedContextBlock(contexts: ReadonlyArray<QuotedContextSelection>): string {
  const normalizedContexts = contexts
    .map((context) => normalizeQuotedContextSelection(context))
    .filter((context): context is QuotedContextSelection => context !== null);
  if (normalizedContexts.length === 0) {
    return "";
  }
  return QUOTED_CONTEXT_TAGS.map((tag) =>
    buildSingleTagQuotedContextBlock(
      tag,
      normalizedContexts.filter((context) => quotedContextTagFor(context) === tag),
    ),
  )
    .filter((block) => block.length > 0)
    .join("\n\n");
}

export function materializeInlineQuotedContextPrompt(
  prompt: string,
  contexts: ReadonlyArray<QuotedContextSelection>,
): string {
  let nextContextIndex = 0;
  let result = "";

  for (const char of prompt) {
    if (char !== INLINE_QUOTED_CONTEXT_PLACEHOLDER) {
      result += char;
      continue;
    }
    const context = contexts[nextContextIndex] ?? null;
    nextContextIndex += 1;
    if (!context) {
      continue;
    }
    result += formatInlineQuotedContextLabel(context);
  }

  return result;
}

export function appendQuotedContextsToPrompt(
  prompt: string,
  contexts: ReadonlyArray<QuotedContextSelection>,
): string {
  const trimmedPrompt = materializeInlineQuotedContextPrompt(prompt, contexts).trim();
  const contextBlock = buildQuotedContextBlock(contexts);
  if (contextBlock.length === 0) {
    return trimmedPrompt;
  }
  return trimmedPrompt.length > 0 ? `${trimmedPrompt}\n\n${contextBlock}` : contextBlock;
}

// ── Parsing back out of a sent message ──────────────────────────────────────────

export interface ExtractedQuotedContexts {
  promptText: string;
  contextCount: number;
  previewTitle: string | null;
  contexts: ParsedQuotedContextEntry[];
}

// Backreference on the tag name so an opening tag only ever closes with its own kind.
const QUOTED_CONTEXT_BLOCK_PATTERN = /<(terminal_context|quoted_message)>\n([\s\S]*?)\n<\/\1>/g;

function isWhitespaceOnly(value: string): boolean {
  return value.trim().length === 0;
}

// Walks blocks from the end backwards, keeping the contiguous run that reaches the end of the
// prompt. Doing it positionally rather than with one greedy regex keeps a block that appears in
// the middle of the prompt (quoted by the user, say) from being swallowed as context.
function findTrailingQuotedContextBlocks(prompt: string): {
  startIndex: number;
  blocks: string[];
} | null {
  const matches = [...prompt.matchAll(QUOTED_CONTEXT_BLOCK_PATTERN)];
  if (matches.length === 0) {
    return null;
  }

  let runStart: number | null = null;
  let previousStart = prompt.length;
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index]!;
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;
    if (!isWhitespaceOnly(prompt.slice(matchEnd, previousStart))) {
      break;
    }
    runStart = matchStart;
    previousStart = matchStart;
  }

  if (runStart === null) {
    return null;
  }

  return {
    startIndex: runStart,
    blocks: matches.filter((match) => match.index >= runStart).map((match) => match[2] ?? ""),
  };
}

function parseQuotedContextEntries(block: string): ParsedQuotedContextEntry[] {
  const entries: ParsedQuotedContextEntry[] = [];
  let current: { header: string; bodyLines: string[] } | null = null;

  const commitCurrent = () => {
    if (!current) {
      return;
    }
    entries.push({
      header: current.header,
      body: current.bodyLines.join("\n").trimEnd(),
    });
    current = null;
  };

  for (const rawLine of block.split("\n")) {
    const headerMatch = /^- (.+):$/.exec(rawLine);
    if (headerMatch) {
      commitCurrent();
      current = {
        header: headerMatch[1]!,
        bodyLines: [],
      };
      continue;
    }
    if (!current) {
      continue;
    }
    if (rawLine.startsWith("  ")) {
      current.bodyLines.push(rawLine.slice(2));
      continue;
    }
    if (rawLine.length === 0) {
      current.bodyLines.push("");
    }
  }

  commitCurrent();
  return entries;
}

export function extractTrailingQuotedContexts(prompt: string): ExtractedQuotedContexts {
  const trailing = findTrailingQuotedContextBlocks(prompt);
  if (!trailing) {
    return {
      promptText: prompt,
      contextCount: 0,
      previewTitle: null,
      contexts: [],
    };
  }

  const promptText = prompt.slice(0, trailing.startIndex).replace(/\n+$/, "");
  const parsedContexts = trailing.blocks.flatMap((block) => parseQuotedContextEntries(block));
  return {
    promptText,
    contextCount: parsedContexts.length,
    previewTitle:
      parsedContexts.length > 0
        ? parsedContexts
            .map(({ header, body }) => (body.length > 0 ? `${header}\n${body}` : header))
            .join("\n\n")
        : null,
    contexts: parsedContexts,
  };
}

// ── Inline placeholder arithmetic ───────────────────────────────────────────────

export function countInlineQuotedContextPlaceholders(prompt: string): number {
  let count = 0;
  for (const char of prompt) {
    if (char === INLINE_QUOTED_CONTEXT_PLACEHOLDER) {
      count += 1;
    }
  }
  return count;
}

export function ensureInlineQuotedContextPlaceholders(
  prompt: string,
  quotedContextCount: number,
): string {
  const missingCount = quotedContextCount - countInlineQuotedContextPlaceholders(prompt);
  if (missingCount <= 0) {
    return prompt;
  }
  return `${INLINE_QUOTED_CONTEXT_PLACEHOLDER.repeat(missingCount)}${prompt}`;
}

function isInlineQuotedContextBoundaryWhitespace(char: string | undefined): boolean {
  return char === undefined || char === " " || char === "\n" || char === "\t" || char === "\r";
}

export function insertInlineQuotedContextPlaceholder(
  prompt: string,
  cursorInput: number,
): { prompt: string; cursor: number; contextIndex: number } {
  const cursor = Math.max(0, Math.min(prompt.length, Math.floor(cursorInput)));
  const needsLeadingSpace = !isInlineQuotedContextBoundaryWhitespace(prompt[cursor - 1]);
  const replacement = `${needsLeadingSpace ? " " : ""}${INLINE_QUOTED_CONTEXT_PLACEHOLDER} `;
  const rangeEnd = prompt[cursor] === " " ? cursor + 1 : cursor;
  return {
    prompt: `${prompt.slice(0, cursor)}${replacement}${prompt.slice(rangeEnd)}`,
    cursor: cursor + replacement.length,
    contextIndex: countInlineQuotedContextPlaceholders(prompt.slice(0, cursor)),
  };
}

export function stripInlineQuotedContextPlaceholders(prompt: string): string {
  return prompt.replaceAll(INLINE_QUOTED_CONTEXT_PLACEHOLDER, "");
}

export function removeInlineQuotedContextPlaceholder(
  prompt: string,
  contextIndex: number,
): { prompt: string; cursor: number } {
  if (contextIndex < 0) {
    return { prompt, cursor: prompt.length };
  }

  let placeholderIndex = 0;
  for (let index = 0; index < prompt.length; index += 1) {
    if (prompt[index] !== INLINE_QUOTED_CONTEXT_PLACEHOLDER) {
      continue;
    }
    if (placeholderIndex === contextIndex) {
      return {
        prompt: prompt.slice(0, index) + prompt.slice(index + 1),
        cursor: index,
      };
    }
    placeholderIndex += 1;
  }

  return { prompt, cursor: prompt.length };
}
